import { isValid } from 'date-fns';
import { equal } from 'ezmoney';
import * as F from 'formik';
import * as yup from 'yup';

import {
  PayableRequestValidation,
  Schemas,
} from '@finance-review/models/payable';
import { filterOutUndefinedProperties } from 'common/utils/filterOutUndefinedProperties';
import { areRequiredInvoiceLineItemsMissing } from 'src/core/modules/purchase-orders/models';
import type { PurchaseOrder } from 'src/core/modules/purchase-orders/models';

import * as DraftInvoiceRequest from './draftInvoiceRequest';
import * as DraftInvoiceRequestErrors from './draftInvoiceRequestErrors';
import * as DraftPaymentSchedule from './draftPaymentSchedule';

export interface ValidationContext
  extends PayableRequestValidation.ValidationContext {
  isSupplierValidated?: boolean | undefined;
  isEligibleToSepaPayments: boolean;
  purchaseOrder?: PurchaseOrder;
  isInvoiceNetAmountFeatureEnabled?: boolean;
}

export interface Errors extends PayableRequestValidation.Errors {
  amount?:
    | DraftInvoiceRequestErrors.RequiredError
    | DraftInvoiceRequestErrors.NonPositiveAmountError;
  netAmount?:
    | DraftInvoiceRequestErrors.RequiredError
    | DraftInvoiceRequestErrors.NonPositiveAmountError;
  emissionDate?:
    | DraftInvoiceRequestErrors.RequiredError
    | DraftInvoiceRequestErrors.DateOlderThanMinimumError
    | DraftInvoiceRequestErrors.EmissionDateInTheFuture;
  dueDate?:
    | DraftInvoiceRequestErrors.RequiredError
    | DraftInvoiceRequestErrors.DateLaterThanMaximumError
    | DraftInvoiceRequestErrors.DueDateBeforeEmissionDateError;
  invoiceNumber?: DraftInvoiceRequestErrors.RequiredError;
  paymentDate?: DraftInvoiceRequestErrors.RequiredError;
  paymentReference?:
    | DraftInvoiceRequestErrors.PaymentReferenceMoreThanMaxLengthError
    | DraftInvoiceRequestErrors.PaymentReferenceInvalidFormatError;
  paymentSchedule?:
    | DraftInvoiceRequestErrors.PaymentScheduleNotMatchingAmountError
    | {
        date?: DraftInvoiceRequestErrors.RequiredError;
        amount?:
          | DraftInvoiceRequestErrors.RequiredError
          | DraftInvoiceRequestErrors.NonPositiveAmountError;
      }[];
  supplierId?:
    | DraftInvoiceRequestErrors.RequiredError
    | DraftInvoiceRequestErrors.SupplierNotValidatedError;
  lineItems?: DraftInvoiceRequestErrors.LineItemsRequiredError;
}

export type CoreInvoiceFormikErrors =
  PayableRequestValidation.ToCorePayableErrors<DraftInvoiceRequest.Entity>;
export type FormikErrors =
  PayableRequestValidation.ToPayableErrors<DraftInvoiceRequest.Entity>;

const paymentReferenceRegExp = new RegExp(/^[a-zA-Z0-9/\-?:().,'+\s]*$/);

export const validate = (
  draftInvoiceRequest: DraftInvoiceRequest.Entity,
  validationContext: ValidationContext,
): CoreInvoiceFormikErrors => {
  // If TMP_ANALYTICAL_SPLITS feature is disabled, force netAmount to null
  const isInvoiceNetAmountFeatureEnabled =
    validationContext.isInvoiceNetAmountFeatureEnabled ?? false;
  const draftInvoiceRequestToValidate = isInvoiceNetAmountFeatureEnabled
    ? draftInvoiceRequest
    : { ...draftInvoiceRequest, netAmount: null };

  const payableErrors = PayableRequestValidation.validate(
    draftInvoiceRequestToValidate,
    validationContext,
  );

  try {
    F.validateYupSchema(
      draftInvoiceRequestToValidate,
      draftInvoiceRequestSchema,
      true,
      {
        ...validationContext,
        draftInvoiceRequest: draftInvoiceRequestToValidate,
        isInvoiceNetAmountFeatureEnabled,
      },
    );
    return payableErrors;
  } catch (error) {
    return {
      ...payableErrors,
      ...F.yupToFormErrors<DraftInvoiceRequest.Entity>(error),
    } as CoreInvoiceFormikErrors;
  }
};

export const fromFormikErrors = (formikErrors: FormikErrors): Errors =>
  filterOutUndefinedProperties({
    ...PayableRequestValidation.fromFormikErrors(formikErrors),
    amount: DraftInvoiceRequestErrors.parseAmountError(formikErrors.amount),
    netAmount: DraftInvoiceRequestErrors.parseAmountError(
      (formikErrors as unknown as { netAmount?: string }).netAmount,
    ),
    dueDate: DraftInvoiceRequestErrors.parseFromFormikError<
      | DraftInvoiceRequestErrors.RequiredError
      | DraftInvoiceRequestErrors.DateLaterThanMaximumError
      | DraftInvoiceRequestErrors.DueDateBeforeEmissionDateError
    >(
      [
        DraftInvoiceRequestErrors.requiredError,
        DraftInvoiceRequestErrors.dateLaterThanMaximumError,
        DraftInvoiceRequestErrors.dueDateBeforeEmissionDateError,
      ],
      formikErrors.dueDate,
      DraftInvoiceRequestErrors.requiredError,
    ),
    emissionDate: DraftInvoiceRequestErrors.parseFromFormikError<
      | DraftInvoiceRequestErrors.RequiredError
      | DraftInvoiceRequestErrors.DateOlderThanMinimumError
      | DraftInvoiceRequestErrors.EmissionDateInTheFuture
    >(
      [
        DraftInvoiceRequestErrors.requiredError,
        DraftInvoiceRequestErrors.dateOlderThanMinimumError,
        DraftInvoiceRequestErrors.emissionDateInTheFuture,
      ],
      formikErrors.emissionDate,
      DraftInvoiceRequestErrors.requiredError,
    ),
    invoiceNumber: DraftInvoiceRequestErrors.parseToRequiredError(
      formikErrors.invoiceNumber,
    ),
    paymentDate: DraftInvoiceRequestErrors.parseToRequiredError(
      formikErrors.paymentDate,
    ),
    paymentReference: DraftInvoiceRequestErrors.parsePaymentReferenceError(
      formikErrors.paymentReference,
    ),
    paymentSchedule: DraftInvoiceRequestErrors.parsePaymentScheduleError(
      formikErrors.paymentSchedule as F.FormikErrors<DraftPaymentSchedule.Entity>,
    ),
    supplierId: DraftInvoiceRequestErrors.parseSupplierError(
      formikErrors.supplierId,
    ),
    lineItems: DraftInvoiceRequestErrors.parseLineItemsError(
      typeof formikErrors.lineItems === 'string'
        ? formikErrors.lineItems
        : undefined,
    ),
  });

const getIsSupplierValidated = (
  { supplierName, supplierId }: DraftInvoiceRequest.Entity,
  { isSupplierValidated }: ValidationContext,
): boolean => {
  // new supplier that does not exist in the back-end
  if (supplierName && !supplierId) {
    return false;
  }

  // in case of undefined, it means we don't know if it was validated. We leave it to the back-end to check
  return isSupplierValidated ?? true;
};

const isPaymentScheduleAmountValid = (
  draftInvoiceRequest: DraftInvoiceRequest.Entity,
): boolean => {
  const totalAmount = DraftInvoiceRequest.getTotalAmount(draftInvoiceRequest);

  // we compare the payment schedule to the total amount of the invoice (including credit note)
  // if the invoice totalAmount is not defined, we can't validate the payment schedule so we return true
  // if the invoice totalAmount is defined and matches the payment schedule amount, we return true
  return (
    totalAmount === undefined ||
    equal(
      DraftPaymentSchedule.computeTotal(
        draftInvoiceRequest.paymentSchedule,
        draftInvoiceRequest.currency,
      ),
      totalAmount,
    )
  );
};

const amountFieldSchema = Schemas.amount.moreThan(
  0,
  DraftInvoiceRequestErrors.nonPositiveAmountError,
);

const draftScheduledPaymentSchema = yup.object().shape({
  id: Schemas.optionalText,
  amount: amountFieldSchema,
  date: Schemas.date,
});

const draftPaymentScheduleSchema = yup
  .array()
  .of(draftScheduledPaymentSchema)
  .min(1)
  .test(
    'amountMatch',
    DraftInvoiceRequestErrors.paymentScheduleNotMatchingAmountError,
    (value, context) => {
      if (!value) {
        return false;
      }
      return isPaymentScheduleAmountValid(context.parent);
    },
  );

const supplierSchema = yup
  .string()
  .test(
    'supplierInvalid',
    DraftInvoiceRequestErrors.supplierNotValidatedError,
    (_, context) =>
      getIsSupplierValidated(
        context.parent,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (context as yup.TestContext<ValidationContext>).options.context!,
      ),
  )
  .test('supplierRequired', DraftInvoiceRequestErrors.requiredError, (value) =>
    Boolean(value),
  );

const paymentReferenceSchema = yup.mixed().when('paymentMethod', {
  is: 'wireTransfer',
  then: () =>
    Schemas.optionalText
      .max(
        DraftInvoiceRequest.paymentReferenceMaxLength,
        DraftInvoiceRequestErrors.paymentReferenceMoreThanMaxLengthError,
      )
      .matches(
        paymentReferenceRegExp,
        DraftInvoiceRequestErrors.paymentReferenceInvalidFormatError,
      ),
  otherwise: () => Schemas.optionalText,
});

const lineItemsSchema = yup
  .mixed()
  .test(
    'lineItemsRequired',
    DraftInvoiceRequestErrors.lineItemsRequiredError,
    (_value, context) => {
      const validationContext = (context as yup.TestContext<ValidationContext>)
        .options.context;
      if (!validationContext?.purchaseOrder) {
        return true; // No validation if no purchase order
      }

      const areRequired = areRequiredInvoiceLineItemsMissing(
        validationContext.purchaseOrder,
        context.parent.lineItems || [],
      );

      return !areRequired;
    },
  );

const draftInvoiceRequestSchema = yup.object().shape({
  amount: amountFieldSchema,
  // netAmount is only validated if TMP_ANALYTICAL_SPLITS feature flag is enabled
  netAmount: yup.mixed().when('$isInvoiceNetAmountFeatureEnabled', {
    is: true,
    then: () =>
      Schemas.optionalAmount
        .moreThan(0, DraftInvoiceRequestErrors.nonPositiveAmountError)
        .nullable(),
    otherwise: () => yup.mixed().nullable(),
  }),
  dueDate: Schemas.date.when('emissionDate', ([emissionDate], schema) =>
    isValid(emissionDate)
      ? schema.min(
          emissionDate,
          DraftInvoiceRequestErrors.dueDateBeforeEmissionDateError,
        )
      : schema,
  ),
  emissionDate: yup.lazy(() =>
    Schemas.date.max(
      new Date(),
      DraftInvoiceRequestErrors.emissionDateInTheFuture,
    ),
  ),
  invoiceNumber: Schemas.text,
  paymentDate: yup.date().when('paymentMethod', {
    is: 'directDebit',
    then: () => Schemas.date,
    otherwise: () => Schemas.optionalDate,
  }),
  paymentReference: paymentReferenceSchema,
  paymentSchedule: yup.mixed().when('$draftInvoiceRequest', {
    is: (draftInvoiceRequest: DraftInvoiceRequest.Entity) =>
      DraftInvoiceRequest.hasPaymentsToSchedule(draftInvoiceRequest),
    then: () => draftPaymentScheduleSchema,
    otherwise: () => yup.mixed(),
  }),
  supplierId: supplierSchema,
  lineItems: lineItemsSchema,
});
